import {UrlCodec} from '../../utils/urlState';
import {PlayerScope} from './api';

export interface ComparisonDiscoveryState {
  q: string;
  page: number;
}

export interface ExternalComparisonDiscoveryState extends ComparisonDiscoveryState {
  watching: boolean;
}

export interface ComparisonUrlState {
  players: string[];
  scope: PlayerScope;
  squad: ComparisonDiscoveryState;
  external: ExternalComparisonDiscoveryState;
}

export const canonicalComparisonSelection = (values: readonly string[]): string[] => {
  const unique: string[] = [];
  values.forEach(value => {
    if (/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(value)
      && !unique.includes(value)
      && unique.length < 4) unique.push(value);
  });
  return unique;
};

export const comparisonCodec: UrlCodec<string[]> = {
  parse: params => canonicalComparisonSelection(params.getAll('player')),
  serialize: selection => {
    const params = new URLSearchParams();
    canonicalComparisonSelection(selection).forEach(id => params.append('player', id));
    return params;
  },
};

const parsePage = (value: string | null): number => {
  if (value === null || !/^[1-9]\d*$/.test(value)) return 1;
  const page = Number(value);
  return Number.isSafeInteger(page) && page <= 2147483647 ? page : 1;
};

export const comparisonPageCodec: UrlCodec<ComparisonUrlState> = {
  parse: params => ({
    players: canonicalComparisonSelection(params.getAll('player')),
    scope: params.get('scope') === 'external' ? 'external' : 'squad',
    squad: {q: params.get('squad_q')?.trim() ?? '', page: parsePage(params.get('squad_page'))},
    external: {
      q: params.get('external_q')?.trim() ?? '',
      page: parsePage(params.get('external_page')),
      watching: params.getAll('external_watching').length === 1 && params.get('external_watching') === 'true',
    },
  }),
  serialize: state => {
    const params = comparisonCodec.serialize(state.players);
    if (state.scope === 'external') params.set('scope', 'external');
    if (state.squad.q) params.set('squad_q', state.squad.q.trim());
    if (state.squad.page > 1) params.set('squad_page', String(state.squad.page));
    if (state.external.q) params.set('external_q', state.external.q.trim());
    if (state.external.page > 1) params.set('external_page', String(state.external.page));
    if (state.external.watching) params.set('external_watching', 'true');
    return params;
  },
};

export const updateComparisonSelection = (selection: readonly string[], playerID: string) => {
  const canonical = canonicalComparisonSelection(selection);
  if (canonical.includes(playerID)) {
    return {selection: canonical.filter(id => id !== playerID), rejected: false};
  }
  if (canonical.length === 4) return {selection: canonical, rejected: true};
  return {selection: [...canonical, playerID], rejected: false};
};
